/**
 * Type for deeply partial objects
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

/**
 * Type guard to check if a value is a non-null object
 */
export function isObject(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === 'object' && !Array.isArray(item)
}

/**
 * Deep merge two objects, combining their properties recursively.
 * Arrays are concatenated, and objects are merged recursively.
 * @param target The target object to merge into
 * @param source The source object to merge from
 * @returns A new object containing the merged result
 */
export function deepMerge<T extends object>(
  target: T,
  source: DeepPartial<T>,
): T {
  const result = { ...target } as Record<string, unknown>

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const sourceValue = source[key]
        const targetValue = target[key as keyof T]

        if (isObject(sourceValue)) {
          if (isObject(targetValue)) {
            result[key] = deepMerge(
              targetValue as Record<string, unknown>,
              sourceValue as Record<string, unknown>,
            )
          } else {
            result[key] = sourceValue
          }
        } else if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
          result[key] = [...targetValue, ...sourceValue]
        } else if (sourceValue !== undefined) {
          result[key] = sourceValue
        }
      }
    }
  }

  return result as T
}
